import { useState } from 'react';
import { CalendarClock, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

const toLocalInput = (iso) => {
  const d = new Date(iso || Date.now());
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

// Initiator-only: move a scheduled call to a new time in one tap (works before or after the original time).
export default function RescheduleButton({ schedule, onDone, compact = false }) {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [when, setWhen] = useState(() => toLocalInput(Date.now() + 15 * 60000));
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!when) { toast.error('Pick a date and time'); return; }
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/calls/scheduled/${schedule.schedule_id}/reschedule`, {
        method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ scheduled_at: new Date(when).toISOString() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success('Call rescheduled', { description: new Date(d.scheduled_at).toLocaleString([], { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) });
      window.dispatchEvent(new Event('scheduled-call-changed'));
      setOpen(false);
      if (onDone) onDone(d);
    } catch (e) { toast.error(e.message || 'Could not reschedule'); } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <Button size={compact ? 'icon' : 'sm'} variant={compact ? 'ghost' : 'outline'} className={compact ? 'h-7 w-7 text-blue-600' : 'h-8'} onClick={() => setOpen(true)} title="Reschedule" data-testid={`reschedule-btn-${schedule.schedule_id}`}>
        <CalendarClock className={compact ? 'w-3.5 h-3.5' : 'w-4 h-4 mr-1.5'} />{!compact && 'Reschedule'}
      </Button>
    );
  }
  return (
    <div className="flex items-center gap-1 w-full" data-testid={`reschedule-form-${schedule.schedule_id}`}>
      <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} className="h-8 text-xs flex-1 min-w-0" data-testid={`reschedule-datetime-${schedule.schedule_id}`} />
      <Button size="icon" className="h-8 w-8 bg-forest-700 hover:bg-forest-800 shrink-0" onClick={save} disabled={saving} title="Save new time" data-testid={`reschedule-save-${schedule.schedule_id}`}><Check className="w-4 h-4" /></Button>
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => setOpen(false)} data-testid={`reschedule-cancel-${schedule.schedule_id}`}><X className="w-4 h-4" /></Button>
    </div>
  );
}
